/**
 * Role-check helpers.
 *
 * Backend role strings ship with inconsistent casing — `'Admin'`, `'admin'`,
 * `'Distributor Admin'`, etc. — and some legacy code accesses them via
 * `user.Role` (capital R, the database column) while others use `user.role`
 * (lowercase, the JWT payload). Doing this comparison inline at call sites
 * is fragile: a single `=== 'Admin'` check silently breaks when the source
 * stores `'admin'`, which has happened in this codebase before.
 *
 * Use the helpers below instead of inline string equality. They normalize
 * whitespace + case once, then check against the canonical lowercase role
 * names that match the backend's `phiAccessService.INTERNAL_PHI_ROLES`
 * (admin, manager, user) and the `AdminRoute` / `OrgRoute` guards.
 */

// Minimal subset of the User shape we need. Avoids a hard dependency on the
// full UserContext type so this helper is safe to import from anywhere.
export interface RoleBearer {
  Role?: string | null;
  role?: string | null;
}

/**
 * Returns the user's role as a normalized (trimmed, lowercased) string, or
 * an empty string if no role is present.
 *
 * Uses `||` (falsy fall-through) rather than `??` (nullish only) so an
 * empty-string `Role` does not short-circuit before trying `role` — some
 * backend rows ship with `Role = ''` from a NOT-NULL DEFAULT column.
 */
export const getNormalizedRole = (user: RoleBearer | null | undefined): string => {
  if (!user) return '';
  const raw = user.Role || user.role || '';
  return String(raw).trim().toLowerCase();
};

/**
 * Platform Admin — the highest-privilege role. Used to gate destructive
 * actions on shared resources (products, TRG letters, the catalog-wide
 * admin sidebar section).
 *
 * Do NOT use this for "any kind of admin" — Org Admin, Client Admin, and
 * Clinic Admin all have their own scoped admin powers but are NOT platform
 * admins. Use the more specific helper if you mean one of those.
 */
export const isPlatformAdmin = (user: RoleBearer | null | undefined): boolean =>
  getNormalizedRole(user) === 'admin';

/**
 * Internal staff — Admin, Manager, or User. Mirrors the backend's
 * `phiAccessService.INTERNAL_PHI_ROLES` set. These roles automatically have
 * full PHI access by role (no per-user toggle needed) and can reach the
 * internal sidebar (Dashboard, Patients, Submissions, etc.).
 */
export const isInternalStaff = (user: RoleBearer | null | undefined): boolean => {
  const role = getNormalizedRole(user);
  return role === 'admin' || role === 'manager' || role === 'user';
};

/**
 * Organization-tier admin (full Org Admin, not the read-only Org Rep).
 */
export const isOrgAdmin = (user: RoleBearer | null | undefined): boolean =>
  getNormalizedRole(user) === 'org admin';

/**
 * Any organization-tier user (Org Admin or Org Rep).
 */
export const isOrgUser = (user: RoleBearer | null | undefined): boolean => {
  const role = getNormalizedRole(user);
  return role === 'org admin' || role === 'org rep';
};

/**
 * Affiliate-tier Client (not Client Admin). Used by the staff Layout when
 * deciding which navigation block to render for a role that has been routed
 * through the wrong layout.
 */
export const isClient = (user: RoleBearer | null | undefined): boolean =>
  getNormalizedRole(user) === 'client';

/**
 * Affiliate Admin.
 */
export const isClientAdmin = (user: RoleBearer | null | undefined): boolean =>
  getNormalizedRole(user) === 'client admin';

/**
 * Standalone Rep (no management-group binding). A Rep with a `distributorID`
 * is treated by other code as a distributor view-only user; this helper
 * intentionally does not differentiate — call sites that care about MG
 * binding should inspect `user.distributorID` themselves.
 */
export const isRep = (user: RoleBearer | null | undefined): boolean =>
  getNormalizedRole(user) === 'rep';

/**
 * Canonical post-login landing path for a user's role — the single source of
 * truth for "where does this user go after signing in." Mirrors the role
 * dispatch in `RoleBasedRedirect` and `LandingPage`.
 *
 * The auth screens (Login, MFAVerification) use this to navigate a freshly
 * authenticated user STRAIGHT to their real home via client-side routing,
 * instead of a full-page reload to a generic destination (`/` bounces through
 * the public landing page; `/dashboard` is admin-only and ejects non-internal
 * users back out through a guard). Returns `/login` for an unknown/empty role.
 */
export const getRoleHomePath = (
  user: (RoleBearer & { distributorID?: number | null }) | null | undefined,
): string => {
  const role = getNormalizedRole(user);
  switch (role) {
    case 'org admin':
    case 'org rep':
      return '/org/dashboard';
    case 'distributor':
    case 'distributor admin':
      return '/distributor/dashboard';
    case 'distributor rep':
      return '/distributor/submissions';
    case 'rep':
      // Distributor-linked reps use the distributor layout; standalone reps
      // get the rep portal. Matches RoleBasedRedirect.
      return user?.distributorID ? '/distributor/submissions' : '/rep/crm';
    case 'clinic admin':
    case 'clinic user':
    case 'client':
    case 'client admin':
      return '/client/patients';
    case 'admin':
    case 'manager':
    case 'user':
      return '/dashboard';
    default:
      return '/login';
  }
};
